import React from 'react';
import { Link } from 'react-router-dom';

const FLOW_STEPS = [
  { key: 'intake', title: 'Intake', detail: 'Citizens and staff submit permits with validated coordinates and audit metadata.' },
  { key: 'review', title: 'Review', detail: 'Workflow states from intake through review, notice, and violation handling.' },
  { key: 'map', title: 'Map', detail: 'Status-colored map operations for field teams — edit and save in one system.' },
  { key: 'timeline', title: 'Timeline', detail: 'Case history and operational events for accountability.' },
  { key: 'audit', title: 'Audit', detail: 'Server-side audit trail on create and status changes.' },
  { key: 'export', title: 'Export', detail: 'CSV exports for permits, cases, and violations from the same store as the UI.' },
];

const PILOT_CONTACT_EMAIL = 'support@sdpanthers.com';
const PILOT_MAILTO = `mailto:${PILOT_CONTACT_EMAIL}?subject=${encodeURIComponent('RobinhoodSpace Pilot Request')}`;

/**
 * GOVOPS-1125 — Public pilot demo launcher (no auth, no GovOps data).
 */
const PilotDemoPage = ({ user, isAdmin }) => (
  <div className="rhs-pilot-demo">
    <header className="rhs-pilot-demo-hero">
      <p className="rhs-pilot-demo-eyebrow">RobinhoodSpace</p>
      <h1>RobinhoodSpace Pilot Demo</h1>
      <p className="rhs-pilot-demo-subtitle">
        Governed operational workflows between government and people
      </p>
      <p className="rhs-pilot-demo-lead">
        False Alarm Permit is the first production workflow — intake, map operations,
        executive dashboards, and exportable evidence on live infrastructure.
      </p>
      <div className="rhs-pilot-demo-cta">
        <Link to="/" className="rhs-btn rhs-btn--primary">
          Launch secure app
        </Link>
        <Link to="/demo/deck" className="rhs-btn rhs-btn--secondary">
          View presenter deck
        </Link>
        <a href={PILOT_MAILTO} className="rhs-btn rhs-btn--secondary">
          Contact for pilot
        </a>
      </div>
    </header>

    <main className="rhs-pilot-demo-main">
      <section className="rhs-pilot-demo-section">
        <h2>What it is</h2>
        <p>
          RobinhoodSpace is municipal <strong>false-alarm permit operations</strong> for
          cities, oversight agencies, and alarm companies. Staff and citizens work in one
          governed system — not spreadsheets, disconnected maps, and inbox tickets.
        </p>
        <p className="rhs-muted">
          The operational engine runs on governed infrastructure with workflow states,
          audit events, and organizational scoping. The public brand is RobinhoodSpace;
          buyers see operations, not internal platform names.
        </p>
      </section>

      <section className="rhs-pilot-demo-section">
        <h2>First workflow: False Alarm Permit</h2>
        <ul className="rhs-pilot-demo-list">
          <li>Unified permit intake (dashboard, map submit, full form)</li>
          <li>Map with status-colored markers and staff edit/patch</li>
          <li>Admin KPI dashboard — totals, workflow breakdown, activity feed</li>
          <li>Case timeline and printable notices (browser PDF)</li>
          <li>CSV export center for permits, cases, and violations</li>
        </ul>
      </section>

      <section className="rhs-pilot-demo-section">
        <h2>5-minute walkthrough</h2>
        <p className="rhs-muted rhs-pilot-demo-walkthrough-intro">
          After you launch the app and sign in with a pilot account:
        </p>
        <ol className="rhs-pilot-demo-flow">
          {FLOW_STEPS.map((step, i) => (
            <li key={step.key}>
              <span className="rhs-pilot-demo-flow-num">{i + 1}</span>
              <div>
                <strong>{step.title}</strong>
                <p className="rhs-muted">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="rhs-muted">
          Golden path: <code>/dashboard</code> → <code>/map</code> → <code>/admin</code> → case timeline.
        </p>
      </section>

      <aside className="rhs-pilot-demo-notice" role="note">
        <strong>Live operational data requires authorized GovOps access.</strong>
        <p>
          This page is public outreach only. Permits, cases, violations, and exports are
          available after sign-in with an entitled pilot account. No protected data is shown here.
        </p>
      </aside>

      {user && (
        <section className="rhs-pilot-demo-section rhs-pilot-demo-signed-in">
          <h2>Signed in</h2>
          <p className="rhs-muted">Continue into the live application.</p>
          <div className="rhs-pilot-demo-cta">
            <Link to={isAdmin ? '/admin' : '/dashboard'} className="rhs-btn rhs-btn--primary">
              Open {isAdmin ? 'admin dashboard' : 'dashboard'}
            </Link>
            <Link to="/map" className="rhs-btn rhs-btn--secondary">
              Open map
            </Link>
          </div>
        </section>
      )}
    </main>

    <footer className="rhs-pilot-demo-footer">
      <p>
        <Link to="/">Sign in</Link>
        {' · '}
        <Link to="/signup">Sign up</Link>
        {' · '}
        <a href={PILOT_MAILTO}>Request pilot</a>
      </p>
    </footer>
  </div>
);

export default PilotDemoPage;
